import {ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnInit, Output, ViewChild} from '@angular/core';
import {ScriptService} from "../../services/scripts/script-service";
import {MaintenanceManagerService} from "../../services/maintenance-manager.service";
import {VideocallComponent} from "../videocall/videocall.component";
import {SettingsManagerService} from "../../services/settings-manager.service";
import {Assistance, AssistanceType} from "../../models/assistance";
import {DeviceType} from "../../models/videocall/videocall-status";
import {AccountManagerService} from "../../services/account-manager-service";
import {TimelineComponent, TimelineGroup, TimelineUpdate} from "../timeline/timeline.component";
import {UiManagerService} from "../../services/ui-manager.service";
import {TranslatePipe} from "@ngx-translate/core";
import {DEBUG, DebugService} from "../../services/debug.service";
import {ITicket} from "@shared/models/ticket";
import {VideocallV2Component} from "../videocall-v2/videocall-v2.component";

@Component({
  selector: 'app-assistance-view',
  templateUrl: './assistance-view.component.html',
  styleUrls: ['./assistance-view.component.scss']
})
export class AssistanceViewComponent implements OnInit {

  @Input()
  assistance: Assistance;

  @Input()
  assistanceType: AssistanceType;

  @Input()
  canEditScene: boolean;

  @Input()
  canClose: boolean;

  @Input() userName: string;
  @Input() userSurname: string;
  @Input() userId: string;
  @Input() useCustomName: boolean;

  @Output() onClosing = new EventEmitter<boolean>();
  @Output() onClosed = new EventEmitter<boolean>();

  @ViewChild(VideocallComponent, { static: false })
  videocallComponent: VideocallComponent;

  @ViewChild(VideocallV2Component, { static: false })
  set setVideocallV2Component(value: VideocallV2Component) {
    this.videocallV2Component = value;
    if (this.videocallV2Component) this.videocallV2Component.initVideocallModule();
  }
  videocallV2Component: VideocallV2Component;

  @ViewChild(TimelineComponent, { static: false })
  stepExecution: TimelineComponent;

  @Input()
  canRecord: boolean;

  isVideocallStarted = false;
  versionInUse = 0;
  mode: 'ar' | 'noar' = 'noar';
  isFullscreen = false;

  /* Maintenance progress */
  progress: TimelineGroup[];

  constructor(
    public accountManagerService: AccountManagerService,
    private maintenanceManager: MaintenanceManagerService,
    private settingsManagerService: SettingsManagerService,
    private debugService: DebugService,
    private uiManagerService: UiManagerService,
    private scriptService: ScriptService,
    public cdr: ChangeDetectorRef,
    private translatePipe: TranslatePipe,
    private ngZone: NgZone
  ) {

    // Bind videocall open action
    this.maintenanceManager.openVideoCall = () => this.startVideocall();

  }

  async ngOnInit(): Promise<void> {

    // Get maintenance progress
    if (this.assistance.maintenance && this.assistance.maintenance.procedure) {
      this.progress = (await this.maintenanceManager.getMaintenanceProgress(this.assistance.maintenance));
    }

  }

  getUser() {
    return {
      id: this.accountManagerService.account.authProviderId,
      firebaseUid: this.accountManagerService.account.authProviderId,
      fcmToken: '',
      firstName: this.useCustomName ? `${this.userName} ${this.userSurname}` : this.accountManagerService.getCurrentUser().displayName,
      lastName: '',
      phoneNumber: '',
      isAnonymous: this.accountManagerService.account.isAnonymous
    };
  }

  getTicket(): ITicket {
    return {
      closingTs: null,
      creationTs:  null,
      description: this.getChatTitle(),
      id: this.assistance.id,
      lastUpdate: null,
      operatorProfile: null,
      requesterProfile: null,
      result: null,
      status: this.assistance.status == 2 || this.assistanceType == AssistanceType.TRAINING || this.assistanceType == AssistanceType.EXTERNAL_MAINTENANCE ? 1 : 2,
      takeInChargeTs: null,
      isServer: this.isVideocallStarted ? 0 : 1,
    };
  }

  getChatTitle() {
    return this.isVideocallStarted || this.assistanceType == AssistanceType.TRAINING || !this.assistance.fieldWorker ? 'Live chat' : `Live chat ${this.translatePipe.transform('with')} ` + this.assistance.fieldWorker.name;
  }

  get welcomeMessage() {
    return this.useCustomName ? `${this.userName} ${this.userSurname} ${this.translatePipe.transform('joined the session')}` : `${this.accountManagerService.getCurrentUser().displayName} ${this.translatePipe.transform('joined the session')}`;
  }

  async startVideocall() {

    // Open videocall panel
    this.isVideocallStarted = true;

    // Send start videocall request card
    this.startVideocallRequest();

    const { deviceType, videocallProvider } = await this.maintenanceManager.waitForDeviceToBeReadyFixed(this.assistance, true);

    switch (deviceType) {

      // Hololens => LiveKit
      case DeviceType.HOLOLENS:

        // Use version 2
        this.versionInUse = 2;

        // Set AR mode
        this.mode = 'ar';

        break;

      // Mobile NOAR => LiveKit
      case DeviceType.MOBILE:

        // Use version 2
        this.versionInUse = 2;

        // Set NOAR mode
        this.mode = 'noar';

        break;

      // Mobile AR => Unity
      // TODO clean this up after Consis scientific review
      case DeviceType.MR_MOBILE:

        // If Consis demo use Unity
        // @ts-ignore
        if (this.assistance.maintenance.equipment.model.company.id === 8) {

          // Use version 1 in production
          this.versionInUse = 1;

          // Force change detection
          this.cdr.detectChanges();

          // Fallback to Unity if in production
          this.videocallComponent.prepareVideocall('Unity');

        } else {

          // Use version 2
          this.versionInUse = 2;

          // Set AR mode
          this.mode = 'ar';

        }

        break;
      default:
        this.debugService.error(DEBUG.LIVE_ASSISTANCE, `Device type ${deviceType} not supported`);
    }

  }

  async startVideocallRequest() {

    // Generate new PIN for headset or mobile
    const pin = await this.accountManagerService.requestPIN();

    // DEPRECATED
    // New: Always send a request
    // Old: Send videocall request only for the first time the chat is created
    if (this.assistanceType == AssistanceType.LIVE_MAINTENANCE || this.assistanceType == AssistanceType.EXTERNAL_MAINTENANCE /*|| (this.fakeMaintenance && !(await this.maintenanceManager.isVideocallRequestAlreadySent(this.maintenance.uid)))*/ ) {
      this.maintenanceManager.startVideocallRequest(this.assistance, this.accountManagerService.account.authProviderId, pin);
    }

  }

  async enterVideocall() {

    // TODO check if it is needed yet
    // this.isVideocallCompleted = false;

    if (this.versionInUse === 2) {

      // Send connect command
      this.videocallV2Component.connect(this.assistance.maintenance.remoteExpert.id, this.assistance.maintenance.remoteExpert.email);

    } else {
      await this.videocallComponent.startVideocall();
    }

  }

  closeVideocall(selfDisconnect?: boolean) {

    // Disconnect from server on remote peer close
    if (selfDisconnect) this.videocallV2Component.disconnect();

    // Stop videocall view
    this.isVideocallStarted = false;
    this.versionInUse = 0;

  }

  closeAssistance() {

    if (!this.canCloseMaintenance()) {
      this.uiManagerService.showInfoDialog('Complete all steps', 'You have to complete all pending steps to close the maintenance.');
      return;
    }

    this.uiManagerService.showConfirmDialogWithExtraTags(
      `Close ${this.closeLabel}`,
      `Do you want to close the ${this.closeLabel}?`,
      (tags: string[]) => {
        this.performAssistanceClosing();
        this.maintenanceManager.addTagsToMaintenance(this.assistance.maintenance, tags);
      }
    );
  }

  async performAssistanceClosing() {
    this.isVideocallStarted = false;
    this.versionInUse = 0;
    this.onClosing.emit(true);
    this.videocallComponent?.stopCall();
    if (this.assistanceType == AssistanceType.LIVE_MAINTENANCE) {
      await this.maintenanceManager.closeMaintenance(this.assistance);
    } else if (this.assistanceType == AssistanceType.EXTERNAL_MAINTENANCE) {
      await this.maintenanceManager.closeExternalMaintenance(this.assistance.maintenance);
    }
    this.onClosed.emit(true);
  }

  autostartVideocall() {
    return (this.assistanceType == AssistanceType.TRAINING || this.assistanceType == AssistanceType.EXTERNAL_MAINTENANCE) && this.assistance.status != 3;
  }

  async onStepExecutionUpdate(event: TimelineUpdate) {
    const stepStatus = await this.maintenanceManager.setStepExecution(this.assistance.maintenance.id, this.assistance.maintenance.procedure.id, event.groupId, event.elementId, event.isExecuted);
    const elem = this.progress.find(task => task.id == event.groupId)?.elements.find(step => step.id == event.elementId);
    if (elem) {
      elem.timestamp = new Date(stepStatus.executedAt);
    }
  }

  canCloseMaintenance() {
    return !this.assistance.procedure || (this.assistance.procedure && this.stepExecution.hasCompletedChecks());
  }

  get closeLabel() {
    switch (this.assistanceType) {
      case AssistanceType.LIVE_MAINTENANCE:
      case AssistanceType.EXTERNAL_MAINTENANCE:
        return 'maintenance';
      case AssistanceType.TRAINING:
        return 'training';
      default:
        return 'assistance';
    }
  }

  get AssistanceType() { return AssistanceType; }

}
